import { useCallback, useEffect, useMemo, useState } from 'react';
import { filterMailboxMessages, type DirectMessage, type MailboxFolder } from '../core/models/DirectMessage';
import type { IMessagingAdapter } from '../adapters/messaging/IMessagingAdapter';
import { useAuth, useData } from '../contexts/index';
import { ROLES } from '../core/constants/roles';
import { normalizeError } from '../core/errors/index';

export type MailboxView = MailboxFolder | 'important' | 'unread';

export function useMailbox() {
  const { user } = useAuth();
  const { messagingAdapter } = useData();
  const [messages, setMessages] = useState<DirectMessage[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [view, setView] = useState<MailboxView>('inbox');
  const [busy, setBusy] = useState(false);
  const [syncNote, setSyncNote] = useState<{
    imported: number;
    matched: number;
    scanned: number;
  } | null>(null);
  const [syncError, setSyncError] = useState('');

  useEffect(() => {
    if (!messagingAdapter || !user?.id) {
      setMessages([]);
      return undefined;
    }
    return messagingAdapter.subscribe(user.id, setMessages);
  }, [messagingAdapter, user?.id]);

  // Clear selection when it disappears from the active folder.
  useEffect(() => {
    if (selectedId && !messages.some((m) => m.id === selectedId && !m.deletedAt)) {
      setSelectedId(null);
    }
  }, [messages, selectedId]);

  const filtered = useMemo(
    () => filterMailboxMessages(messages, view),
    [messages, view],
  );

  const selected = useMemo(
    () => filtered.find((m) => m.id === selectedId) ?? null,
    [filtered, selectedId],
  );

  const counts = useMemo(() => ({
    inbox: filterMailboxMessages(messages, 'inbox').length,
    unread: filterMailboxMessages(messages, 'unread').length,
    important: filterMailboxMessages(messages, 'important').length,
    drafts: filterMailboxMessages(messages, 'drafts').length,
    sent: filterMailboxMessages(messages, 'sent').length,
  }), [messages]);

  const canSync = user?.role === ROLES.GOD || user?.role === ROLES.SUPPORT_AGENT;

  const patch = useCallback(async (
    ids: string[],
    update: { read?: boolean; important?: boolean; deleted?: boolean },
  ) => {
    if (!messagingAdapter || ids.length === 0) return;
    setBusy(true);
    try {
      await messagingAdapter.updateMessages({ messageIds: ids, ...update });
    } finally {
      setBusy(false);
    }
  }, [messagingAdapter]);

  const markRead = useCallback((id: string, read = true) => patch([id], { read }), [patch]);
  const markImportant = useCallback(
    (id: string, important: boolean) => patch([id], { important }),
    [patch],
  );
  const remove = useCallback(async (id: string) => {
    await patch([id], { deleted: true });
    setSelectedId(null);
  }, [patch]);

  const sync = useCallback(async () => {
    if (!messagingAdapter?.syncFromResend || !canSync) return;
    setBusy(true);
    setSyncNote(null);
    setSyncError('');
    try {
      const result = await messagingAdapter.syncFromResend();
      setSyncNote({
        imported: result.imported,
        matched: result.matched ?? result.imported,
        scanned: result.scanned,
      });
    } catch (err) {
      const appErr = normalizeError(err, { operation: 'syncSupportInbox' });
      const detail = String(
        appErr.details.sourceMessage
        ?? appErr.details.causeMessage
        ?? appErr.message,
      );
      setSyncError(detail);
    } finally {
      setBusy(false);
    }
  }, [messagingAdapter, canSync]);

  const saveDraft = useCallback(async (
    input: Parameters<IMessagingAdapter['saveDraft']>[0],
  ) => {
    if (!messagingAdapter) throw new Error('Messaging unavailable');
    return messagingAdapter.saveDraft(input);
  }, [messagingAdapter]);

  return {
    messages: filtered,
    allMessages: messages,
    selected,
    selectedId,
    setSelectedId,
    view,
    setView,
    counts,
    busy,
    syncNote,
    syncError,
    canSync,
    markRead,
    markImportant,
    remove,
    sync,
    saveDraft,
    messagingAdapter,
  };
}
